import React from 'react';

import { SortableWrapper } from 'src/components/SortableWrapper';
import { DraggableCustomPropertyRow } from 'src/features/crm/properties/CustomPropertyRow';
import { useAppDispatch } from 'src/hooks/useStore';
import { usePatchCompanyMutation } from 'src/services/api/companiesApi';
import { updateClientUser } from 'src/store/clients/actions';
import { AddressCustomFieldValues } from 'src/store/clients/types';

import {
  CustomFieldValueData,
  CustomPropertiesListProps,
} from '../clientDetailsTypes';

interface SortableCustomPropertiesListProps extends CustomPropertiesListProps {
  onSortEnd: (oldIndex: number, newIndex: number) => void;
}

export const SortableCustomPropertiesList: React.FC<
  SortableCustomPropertiesListProps
> = ({
  setPropertiesMenuAnchor,
  anchoredCustomFieldId,
  clientUser,
  company,
  customFields,
  openUpdateCustomFieldMenu,
  onSaveProperty,
  onSortEnd,
}) => {
  const dispatch = useAppDispatch();
  const [patchCompany] = usePatchCompanyMutation();
  const [activeFieldId, setActiveFieldId] = React.useState<string | null>(null);

  /**
   * This method get the custom field value
   * from client customFields by field's id.
   * @param customFieldId the id of the custom field
   */
  const getCustomFieldValue = (
    customFieldId: string,
  ): string | string[] | AddressCustomFieldValues | undefined => {
    if (clientUser?.fields?.customFields) {
      return clientUser.fields.customFields[customFieldId];
    } else if (company?.fields?.customFields) {
      return company.fields.customFields[customFieldId];
    }
    return undefined;
  };

  /**
   * This function updates client user data with
   * custom field value.
   * @param customFieldValue
   * @returns
   */
  const handleUpdateClientCustomFieldValue = async (
    customFieldData: CustomFieldValueData,
  ) => {
    if (!clientUser) {
      // Update company custom field value
      if (!company?.id) {
        return;
      }

      const updatedCompanyCustomFields = {
        ...company.fields.customFields,
        [customFieldData.fieldId]: customFieldData.value || '',
      };

      await patchCompany({
        companyId: company.id,
        customFields: updatedCompanyCustomFields,
      }).unwrap();
      return;
    }
    const updatedClientCustomFields = {
      ...clientUser.fields.customFields,
      [customFieldData.fieldId]: customFieldData.value,
    };
    const updateOptions = {
      showSuccessMessage: false,
      isOptimisticChange: true,
    };
    dispatch(
      updateClientUser(
        {
          userId: clientUser.id,
          cognitoFirstName: clientUser.fields.givenName,
          customFields: updatedClientCustomFields,
          companyId: clientUser.fields.companyId,
          companyName: clientUser.fields.companyName,
          additionalFields: clientUser.additionalFields,
        },
        updateOptions,
      ),
    );
  };

  const sortableIds = (customFields || []).map((f) => f.id);

  return (
    <SortableWrapper
      items={sortableIds}
      onSortEnd={onSortEnd}
      renderOverlay={(activeId) => {
        const customField = (customFields || []).find((f) => f.id === activeId);
        if (!customField) return null;
        return (
          <DraggableCustomPropertyRow
            onSaveProperty={onSaveProperty}
            id={customField.id}
            initialFieldValue={getCustomFieldValue(customField.id) || ''}
            field={{ ...customField }}
            onPropertyNameClicked={() => {}}
            onFieldValueUpdate={handleUpdateClientCustomFieldValue}
            setAsAnchor={false}
            setAnchorEl={setPropertiesMenuAnchor}
            isEditing={false}
            onSetActiveFieldId={setActiveFieldId}
          />
        );
      }}
    >
      <div className="flex flex-col">
        {customFields &&
          customFields.map((customField) => (
            <DraggableCustomPropertyRow
              onSaveProperty={onSaveProperty}
              key={customField.id}
              id={customField.id}
              initialFieldValue={getCustomFieldValue(customField.id) || ''}
              field={{
                ...customField,
              }}
              onPropertyNameClicked={(
                ev: React.MouseEvent<HTMLElement, MouseEvent>,
              ) => {
                openUpdateCustomFieldMenu(ev, customField);
              }}
              onFieldValueUpdate={handleUpdateClientCustomFieldValue}
              setAsAnchor={customField.id === anchoredCustomFieldId}
              setAnchorEl={setPropertiesMenuAnchor}
              isEditing={activeFieldId === customField.id}
              onSetActiveFieldId={setActiveFieldId}
            />
          ))}
      </div>
    </SortableWrapper>
  );
};
